import React from "react";
import { useRouter } from "next/router";

import { constants } from "src/shared/constants";
import { useGlobalStore } from "../hooks/useGlobalStore";
import { useStorageCache } from "../hooks/useStorageCache";
import { useTeamConfig } from "../components/TeamConfigProvider";
import { repairNestedQuery } from "src/utils/repair-nested-query";

import type { TInfoField } from "src/shared/types/globalStotre.types";

const REP_QUERY_KEY = "repAccountId";

/**
 * Captures sales-rep attribution from the landing URL and keeps it for the
 * buying session.
 *
 * Two kinds, both URL-takes-precedence-over-storage:
 *
 * - **info fields** — which query params are tracked, and the Archtics slot each
 *   writes into, come from per-team `ATTRIBUTION_SETTINGS.INFO_FIELDS`. A team
 *   with no config captures nothing, which is the safe default (see the type's
 *   doc comment for why an occupied slot must never be written).
 * - **rep account id** — a fixed `?repAccountId=` param, no config at all,
 *   captured for every team, because the vendor command takes the id directly
 *   instead of a configured slot number.
 *
 * Renders nothing. Mounted once at the app root, so it re-runs on client-side
 * navigation as well as first load.
 */
export function AttributionCaptureProvider(props: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const teamConfig = useTeamConfig();
  const { setGlobalState } = useGlobalStore();

  const infoFieldsStorage = useStorageCache(
    constants.storage.attributionInfoFields
  );
  const repStorage = useStorageCache(constants.storage.attributionRepAccount);

  const teamSchema = teamConfig?.TEAM_SCHEMA;
  const infoFieldConfig = teamConfig?.ATTRIBUTION_SETTINGS?.INFO_FIELDS;

  React.useEffect(() => {
    // `router.query` is empty until Next has parsed the URL, so capturing
    // before this would silently miss the params on a cold load.
    if (!router.isReady) return;
    if (!teamSchema) return;

    // setGlobalState shallow-merges only at the top level, so `attribution`
    // would be replaced wholesale. This effect patches it twice — once for info
    // fields, once for the rep id — so each patch reads the live value rather
    // than a closure that predates the other patch.
    const setAttribution = (patch: {
      infoFields?: TInfoField[];
      repAccountId?: string;
    }) => {
      const current = useGlobalStore.getState().attribution;
      setGlobalState({ attribution: { ...current, ...patch } });
    };

    // Rep links are typed by hand, so a `?` in place of an `&` is a real
    // possibility — and it makes the browser swallow every param after it.
    const query = repairNestedQuery(router.query);

    if (infoFieldConfig) {
      const captured: TInfoField[] = [];

      Object.entries(infoFieldConfig).forEach(([queryKey, config]) => {
        const raw = query?.[queryKey];
        const value = Array.isArray(raw) ? raw[0] : raw;
        if (typeof value === "string" && value.trim() !== "") {
          captured.push({ info_field: config.infoField, value });
        }
      });

      if (captured.length > 0) {
        setAttribution({ infoFields: captured });
        infoFieldsStorage?.set(
          teamSchema,
          captured,
          constants.attributionTtlMs
        );
      } else {
        const cached = infoFieldsStorage?.get(teamSchema);
        if (cached && cached.length > 0) setAttribution({ infoFields: cached });
      }
    }

    const rawRep = query?.[REP_QUERY_KEY];
    const repFromUrl = Array.isArray(rawRep) ? rawRep[0] : rawRep;
    const trimmedRep = typeof repFromUrl === "string" ? repFromUrl.trim() : "";
    // An over-length value is treated as ABSENT — never captured, never stored,
    // never sent. Rejecting it later (at the API or the vendor call) would risk
    // turning a junk link into a failed checkout.
    const repAccountId =
      trimmedRep !== "" &&
      trimmedRep.length <= constants.attributionRepAccountIdMaxLength
        ? trimmedRep
        : "";

    if (repAccountId !== "") {
      setAttribution({ repAccountId });
      repStorage?.set(teamSchema, repAccountId, constants.attributionTtlMs);
    } else {
      const cachedRep = repStorage?.get(teamSchema);
      if (cachedRep) {
        setAttribution({ repAccountId: cachedRep });
      } else {
        // Absent from both URL and storage — reset, so a value from another team
        // schema (or one that expired while this was mounted) cannot keep being
        // sent on a client-side navigation.
        setAttribution({ repAccountId: "" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query, teamSchema]);

  return <>{props.children}</>;
}
